import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import type { ListBindingsResponse } from "@m8t-stack/api-contract";
import { apiCall } from "../../lib/api-client.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import {
  colors,
  renderJson,
  renderTable,
  resolveOutputMode,
} from "../../lib/output.js";

export class BindListCommand extends M8tCommand {
  static paths = [["bind", "list"]];
  static usage = Command.Usage({
    description: "List channel→worker bindings.",
    details:
      "Lists every binding sorted by channel + bindingId. Orphaned bindings are highlighted; the footer shows the orphan count when > 0.",
    examples: [
      ["Pretty table", "$0 bind list"],
      ["Filter by channel", "$0 bind list --channel telegram"],
      ["Filter by status", "$0 bind list --status orphaned"],
      ["Machine-readable JSON", "$0 bind list --output json"],
    ],
  });

  channel = Option.String("--channel");
  status = Option.String("--status");
  output = Option.String("--output");
  subscription = Option.String("--subscription", {
    description: "Azure subscription ID to discover gateway in (defaults to active az subscription).",
  });
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const interactive = (this.context.stdout as NodeJS.WriteStream).isTTY === true;
    const ctx = await resolveGatewayContext({ interactive, subscriptionId: this.subscription, resourceGroup: this.resourceGroup });

    const qs = new URLSearchParams();
    if (this.channel) qs.set("channel", this.channel);
    if (this.status) qs.set("status", this.status);
    const path = qs.toString().length > 0 ? `/api/bindings?${qs.toString()}` : "/api/bindings";

    const data = await apiCall<ListBindingsResponse>(
      { gatewayUrl: ctx.gatewayUrl, gatewayClientId: ctx.gatewayClientId },
      { method: "GET", path },
      (msg) => this.context.stderr.write(msg + "\n"),
    );

    if (mode === "json") {
      this.context.stdout.write(renderJson(data) + "\n");
      return 0;
    }

    if (data.bindings.length === 0) {
      this.context.stdout.write(
        "no bindings yet — try 'm8t bind add telegram --worker <agent> --slug <id> --bot-token <token>'.\n",
      );
      return 0;
    }

    interface Row extends Record<string, unknown> {
      bindingId: string;
      channel: string;
      worker: string;
      status: string;
      botUsername: string;
      created: string;
      createdBy: string;
    }

    const rows: Row[] = data.bindings.map((b) => ({
      bindingId: b.bindingId,
      channel: b.channel,
      worker: b.agentName,
      status: b.status === "orphaned" ? colors.error("⚠ orphaned") : b.status,
      botUsername: b.botUsername ?? "-",
      created: b.createdAt.slice(0, 10),
      createdBy: b.createdBy,
    }));

    const table = renderTable<Row>(rows, [
      { key: "bindingId", label: "BINDING ID" },
      { key: "channel", label: "CHANNEL" },
      { key: "worker", label: "WORKER" },
      { key: "status", label: "STATUS" },
      { key: "botUsername", label: "BOT USERNAME" },
      { key: "created", label: "CREATED" },
      { key: "createdBy", label: "CREATED BY" },
    ]);
    this.context.stdout.write(table + "\n");

    const orphanCount = data.bindings.filter((b) => b.status === "orphaned").length;
    if (orphanCount > 0) {
      this.context.stdout.write(
        `\n${data.bindings.length.toString()} binding(s); ${orphanCount.toString()} orphaned\n`,
      );
    }

    return 0;
  }
}
